import { useState, useEffect, useCallback } from 'react';
import { SetURLSearchParams } from 'react-router-dom';

interface UseQuestionStateProps {
  currentQuestion: number;
  setCurrentQuestion: React.Dispatch<React.SetStateAction<number>>;
  setSearchParams: SetURLSearchParams;
  refine: { what_to_provide: string; examples: string[] }[];
}

interface QuestionState {
  finalAnswers: Record<number, string>;
  textValue: string;
  edit: boolean;
  isPopupOpen: boolean;
}

export const useQuestionState = ({
  currentQuestion,
  setCurrentQuestion,
  setSearchParams,
  refine,
}: UseQuestionStateProps) => {
  const [state, setState] = useState<QuestionState>(() => {
    const savedAnswers = localStorage.getItem("answers");
    return {
      finalAnswers: savedAnswers ? JSON.parse(savedAnswers) : {},
      textValue: "",
      edit: true,
      isPopupOpen: true,
    };
  });

  // Load saved question from localStorage
  useEffect(() => {
    const savedCurrentQuestion = localStorage.getItem("currentQuestion");
    if (savedCurrentQuestion) {
      setCurrentQuestion(Number(savedCurrentQuestion));
    }
  }, [setCurrentQuestion]);

  // Persist current question
  useEffect(() => {
    localStorage.setItem("currentQuestion", String(currentQuestion));
  }, [currentQuestion]);

  // Update text value when question changes
  useEffect(() => {
    const savedAnswers = JSON.parse(localStorage.getItem("answers") || "{}");
    const savedAnswer = savedAnswers[currentQuestion];
    if (savedAnswer) {
      setState(prev => ({
        ...prev,
        textValue: savedAnswer,
        isPopupOpen: false,
        edit: false,
      }));
    } else {
      setState(prev => ({
        ...prev,
        textValue: "",
        isPopupOpen: true,
        edit: true,
      }));
    }
  }, [currentQuestion]);

  // Check if text has been modified
  useEffect(() => {
    if (state.textValue !== state.finalAnswers[currentQuestion]) {
      setState(prev => ({ ...prev, edit: true }));
    }
  }, [state.textValue, currentQuestion, state.finalAnswers]);

  const saveAnswer = useCallback((answer: string) => {
    setState(prev => {
      const updatedAnswers = { ...prev.finalAnswers, [currentQuestion]: answer };
      localStorage.setItem("answers", JSON.stringify(updatedAnswers));
      return {
        ...prev,
        finalAnswers: updatedAnswers,
      };
    });
  }, [currentQuestion]);

  const handleTextChange = useCallback((newText: string) => {
    setState(prev => ({ ...prev, textValue: newText }));
    saveAnswer(newText);
    setState(prev => ({ ...prev, edit: true }));
  }, [saveAnswer]);

  const proceedToNextQuestion = useCallback(() => {
    if (currentQuestion < refine.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSearchParams({ currentQuestion: nextQuestion.toString() });
    }
    setState(prev => ({ ...prev, edit: true }));
  }, [currentQuestion, refine.length, setCurrentQuestion, setSearchParams]);

  const handleBack = useCallback(() => {
    saveAnswer(state.textValue);
    if (currentQuestion > 0) {
      const previousQuestion = currentQuestion - 1;
      setCurrentQuestion(previousQuestion);
    }
  }, [currentQuestion, setCurrentQuestion, saveAnswer, state.textValue]);

  const handleUseExample = useCallback(() => {
    const allExamples = (refine[currentQuestion]?.examples || []).join("\n");
    setState(prev => ({
      ...prev,
      textValue: allExamples,
      isPopupOpen: false,
      edit: false,
    }));
    saveAnswer(allExamples);
  }, [currentQuestion, refine, saveAnswer]);

  const setEdit = useCallback((edit: boolean) => {
    setState(prev => ({ ...prev, edit }));
  }, []);

  const setIsPopupOpen = useCallback((isPopupOpen: boolean) => {
    setState(prev => ({ ...prev, isPopupOpen }));
  }, []);

  return {
    ...state,
    saveAnswer,
    handleTextChange,
    proceedToNextQuestion,
    handleBack,
    handleUseExample,
    setEdit,
    setIsPopupOpen,
  };
};
